import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Banknote, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export function DailyCashBagCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/cash-bag`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      // Silent — admin will see empty card
      console.error("Cash bag load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, [load]);

  // Hide the card entirely when there's nothing to show (no receipts AND no anomalies)
  const isEmpty = !loading && data && data.receipt_count === 0 && (data.anomalies?.length || 0) === 0;
  if (isEmpty) return null;

  return (
    <Card className="mb-4 border-0 shadow-lg rounded-2xl bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white" data-testid="daily-cash-bag-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Banknote className="w-5 h-5" />
            <CardTitle className="text-white text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
              Daily Cash Bag · {data?.date || "today"}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold bg-white/20 backdrop-blur px-3 py-1 rounded-full" data-testid="cash-bag-total">
              {data ? `${fmt(data.total_cash)} · ${data.receipt_count} receipt${data.receipt_count !== 1 ? "s" : ""}` : "—"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              className="h-7 w-7 p-0 text-white hover:bg-white/20"
              disabled={loading}
              data-testid="cash-bag-refresh"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="h-7 w-7 p-0 text-white hover:bg-white/20"
              data-testid="cash-bag-toggle"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-2">
        {/* Per-driver summary (always visible) */}
        {data?.drivers?.length > 0 ? (
          <div className="space-y-1.5">
            {data.drivers.map((d) => (
              <div
                key={d.driver_id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/10 backdrop-blur"
                data-testid={`cash-bag-driver-${d.driver_id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(d.driver_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="font-bold truncate text-sm">{d.driver_name}</p>
                    <p className="text-[10px] text-white/70">{d.receipts.length} receipt{d.receipts.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <span className="font-mono font-bold text-base flex-shrink-0">{fmt(d.total_cash)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-white/80 italic px-1">No driver-side cash payments recorded yet today.</p>
        )}

        {/* Expanded: per-receipt drill-down */}
        {expanded && data?.drivers?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/20 space-y-2 max-h-[220px] overflow-y-auto">
            {data.drivers.map((d) => (
              <div key={d.driver_id} className="bg-white/5 rounded p-2">
                <p className="text-[11px] font-bold text-white/90 mb-1">{d.driver_name}</p>
                {d.receipts.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] py-0.5">
                    <span className="truncate">
                      #{r.booking_conf} · {r.passenger_name}{" "}
                      {!r.fully_paid_after && <span className="text-amber-200">(partial)</span>}
                    </span>
                    <span className="font-mono font-bold flex-shrink-0 ml-2">{fmt(r.amount)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Anomalies row (cash bookings whose payment_status hasn't flipped) */}
        {(data?.anomalies?.length || 0) > 0 && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-amber-400/20 border border-amber-300/50 flex items-start gap-2" data-testid="cash-bag-anomalies">
            <AlertTriangle className="w-4 h-4 text-amber-200 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-100">
                {data.anomalies.length} cash booking{data.anomalies.length !== 1 ? "s" : ""} still flagged as unpaid
              </p>
              {expanded && (
                <div className="mt-1 space-y-0.5">
                  {data.anomalies.map((a) => (
                    <div key={a.id} className="text-[11px] text-amber-50">
                      • {a.full_name} ({a.driver_name}) — owes <span className="font-bold">{fmt(a.balance_due)}</span>
                    </div>
                  ))}
                </div>
              )}
              {!expanded && (
                <p className="text-[10px] text-amber-100/80">Expand to review and settle.</p>
              )}
            </div>
          </div>
        )}

        {/* No anomalies = clear green check */}
        {data?.receipt_count > 0 && (data?.anomalies?.length || 0) === 0 && (
          <Badge variant="outline" className="bg-white/15 border-white/30 text-white text-[10px] mt-1">
            ✓ All cash receipts reconciled
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
